import React, { Fragment, useCallback, useEffect, useState } from "react";
import { isEmpty, map } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Card, Col, Row } from "react-bootstrap";
import { useHistory } from "react-router";
import PageTitle from "../../components/Ui/PageTitle";
import { TRootState } from "../../store/reducers/RootReducer";
import ProductDetails from "../../components/ProductDetails";
import { OrderCategory, TApiOrderDetails } from "../../api/endpoints/Order";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import IconButton from "../../components/Buttons/IconButton";
import { clearBasket, removeProductFromBasket } from "../../store/action-creators/basket";
import CreateOrderButton from "../../components/CreateOrderButton";
import { paths } from "../../router";
import { isLoggedIn } from "../../store/selectors/UserSelectors";
import { TApiSet } from "../../api/endpoints/Set";
import * as Api from "../../api";
import { deriveBcType, deriveDataSetName } from "../../utils/DataSet";
import LoadingIndicator from "../../components/Feedback/LoadingIndicator";
import { TOrderProductData } from "../../api/endpoints/OrderProduct";

interface IProps {
  item: TOrderProductData<OrderCategory.REALTIME>;
  id: string;
  sets: Array<TApiSet>
}

const BasketItem = (props: IProps) => {
  const { id, item, sets } = props;
  const dispatch = useDispatch();
  const set = {
    name: deriveDataSetName(item, sets)
  }
  const bcType = deriveBcType(item, sets);

  const onBasketItemRemove = useCallback(() => {
    dispatch(removeProductFromBasket(id))
  }, [id, dispatch]);

  return (
    <Card className="basket-item">
      <Card.Body>
        <ProductDetails
          set={set}
          productData={item}
          bcType={bcType}
          actions={
            <Fragment>
              <IconButton icon={faTrashAlt} onClick={onBasketItemRemove} variant="danger" title="Remove" />
            </Fragment>
          }
        />
      </Card.Body>
    </Card>
  )
}

const Basket = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isUserLoggedIn = useSelector(isLoggedIn);
  const productDataSet = useSelector<TRootState, TRootState['basket']['data']>(state => state.basket.data);

  const [sets, setSets] = useState<Array<TApiSet> | null>(null);

  useEffect(() => {
    Api.Set.list().then(setSets);
  }, []);

  const onOrderCreate = useCallback((order: TApiOrderDetails) => {
    dispatch(clearBasket());
    history.push(paths.order(order.id));
  }, [dispatch, history])

  if (!sets) {
    return <LoadingIndicator />
  }

  return (
    <div className="basket">
      <Row>
        <Col>
          <PageTitle title="Your basket" />
        </Col>
      </Row>

      <Row>
        <Col>
          {
            map(productDataSet, (product, id) =>
              <BasketItem key={id} id={id} item={product} sets={sets} />)
          }
          {
            isEmpty(productDataSet) &&
            <div className="empty-basket-wrapper">
              There is currently no products in your basket.
            </div>
          }
        </Col>
      </Row>

      <Row className="actions">
        <Col>
          {
            !isEmpty(productDataSet) &&
            <Fragment>
              <CreateOrderButton
                disabled={!isUserLoggedIn}
                orderProps={{
                  category: OrderCategory.REALTIME,
                  folder_id: null,
                  products: Object.values(productDataSet)
                }}
                onSuccess={onOrderCreate}
              />
              {
                !isUserLoggedIn &&
                <span className="login-note">Please log in to create an order.</span>
              }
            </Fragment>
          }
        </Col>
      </Row>
    </div>
  )
}

export default Basket;
